// Encabezado de imports (ajuste de ruta y limpieza de DatePicker/InputNumber)
import React, { useEffect, useState } from 'react';
import { useParams, useNavigate, useLocation } from 'react-router-dom';
import { Typography, Button, Space, Descriptions, Spin, message, Modal, Form, Select, Input, Popconfirm, Tag, Table, Col, Row, Tooltip, Grid, Card, Statistic, Progress, Divider, theme } from 'antd';
import { FileTextOutlined, FilePdfOutlined, ArrowLeftOutlined, MailOutlined, DeleteOutlined, EditOutlined, CloudUploadOutlined, CopyOutlined, BankOutlined, UserOutlined, CheckCircleOutlined, CloseCircleOutlined, EditFilled, CreditCardOutlined } from '@ant-design/icons';
import moment from 'moment';
import { userService } from '../../services/api';
import { useAuth } from '../../contexts/AuthContext';
import AddPaymentModal from './components/AddPaymentModal';
import ResponsiveTable from '../../components/Common/ResponsiveTable';


const { Title, Text } = Typography;

// Cifras monetarias con formato es-MX y tipografía tabular para que no bailen.
const formatMoney = (value, moneda = 'MXN') =>
  `$${Number(value || 0).toLocaleString('es-MX', { minimumFractionDigits: 2, maximumFractionDigits: 2 })} ${moneda}`;

// Metadatos de estado: color, etiqueta e ícono en un solo lugar.
const STATUS_META = {
  timbrada: { color: 'green', label: 'Timbrada', icon: <CheckCircleOutlined /> },
  cancelada: { color: 'red', label: 'Cancelada', icon: <CloseCircleOutlined /> },
  borrador: { color: 'blue', label: 'Borrador', icon: <EditFilled /> },
};
const statusMeta = (s) => STATUS_META[s] || { color: 'default', label: s || '—', icon: null };

const InvoiceDetail = () => {
  const { token } = theme.useToken();
  const { isReadOnly } = useAuth();
  const { id } = useParams();
  const navigate = useNavigate();
  const location = useLocation();
  const backPath = location?.state?.from === 'cobranza' ? '/cobranza/ppd' : '/invoices';
  const screens = Grid.useBreakpoint();
  const [invoice, setInvoice] = useState(null);
  const [loading, setLoading] = useState(true);
  const [cancelModalVisible, setCancelModalVisible] = useState(false);
  const [motives, setMotives] = useState([]);
  const [selectedMotivo, setSelectedMotivo] = useState(null);
  const [cancelling, setCancelling] = useState(false);
  const [cancelForm] = Form.useForm();
  const isTimbrada = invoice?.status === 'timbrada';
  const allowedCodes = ['01', '02', '03', '04'];

  // Estado para pagos relacionados
  const [payments, setPayments] = useState([]);
  const [paymentsLoading, setPaymentsLoading] = useState(false);
  const [totalPaid, setTotalPaid] = useState(0);

  const invoiceTotal = Number(invoice?.total || 0);
  const isInvoiceFullyPaid = totalPaid >= invoiceTotal;

  const loadInvoice = async () => {
    try {
      setLoading(true);
      const resp = await userService.getInvoice(id);
      setInvoice(resp.data);
    } catch (err) {
      console.error(err);
      message.error('No se pudo cargar la factura.');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadInvoice();
  }, [id]);

  // Refrescar factura tras crear/timbrar pago
  const refreshInvoice = async () => {
    try {
      const resp = await userService.getInvoice(id);
      setInvoice(resp.data);
      await loadPayments(resp?.data?.id);
    } catch (e) {
      // ... existing code ...
    }
  };

  // Cargar pagos y filtrar por la factura actual
  const loadPayments = async (invoiceIdArg = null) => {
    const targetId = invoiceIdArg ?? invoice?.id;
    if (!targetId) return;
    setPaymentsLoading(true);
    try {
      const resp = await userService.getPayments(100, 0);
      const payload = resp?.data ?? resp;
      const list = payload?.payments ?? [];
      
      const filtered = list.filter(
        (p) => Array.isArray(p?.documents) && p.documents.some((d) => d?.invoice?.id == targetId)
      );
      setPayments(filtered);

      // Calcular total pagado
      const sum = filtered.reduce((acc, p) => {
        const applied = (p.documents || [])
          .filter((d) => d?.invoice?.id == targetId)
          .reduce((s, d) => s + Number(d?.importe_pagado || 0), 0);
        return acc + applied;
      }, 0);
      setTotalPaid(sum);
    } catch (e) {
      console.error(e);
      message.error('No se pudieron cargar los pagos.');
    } finally {
      setPaymentsLoading(false);
    }
  };

  useEffect(() => {
    if (invoice?.id) {
      loadPayments(invoice.id);
    }
  }, [invoice?.id]);

  const openCancelModal = async () => {
    try {
      const check = await userService.checkInvoiceCancellation(id);
      const canCancel = check?.data?.can_cancel ?? check?.can_cancel;
      const reason = check?.data?.reason ?? check?.reason;
      if (!canCancel) {
        message.warning(reason || 'No se puede cancelar esta factura.');
        return;
      }
      const motivesResp = await userService.getCancellationMotives();
      const motivesData = motivesResp?.data ?? motivesResp;
      const filtered = Array.isArray(motivesData)
        ? motivesData.filter((m) => allowedCodes.includes(m.code))
        : [];
      setMotives(filtered);
      setSelectedMotivo(null);
      cancelForm.resetFields();
      setCancelModalVisible(true);
    } catch (e) {
      message.error('Error al verificar cancelación.');
    }
  };

  const handleConfirmCancel = async (values) => {
    try {
      const motivo = values.motivo;
      const uuidSustitucion = values.uuid_sustitucion || null;

      if (!allowedCodes.includes(motivo)) {
        message.error('Selecciona un motivo válido (01–04).');
        return;
      }

      if ((motivo === '01' || motivo === '04') && !uuidSustitucion) {
        message.error('El motivo seleccionado requiere UUID de sustitución.');
        return;
      }

      setCancelling(true);
      const resp = await userService.cancelInvoice(id, {
        motivo,
        uuid_sustitucion: uuidSustitucion,
      });
      const msg = resp?.message || 'CFDI cancelado exitosamente';
      message.success(msg);
      setCancelModalVisible(false);
      setCancelling(false);
      
      // Recargar factura para actualizar estatus
      loadInvoice();

      
    } catch (err) {
      setCancelling(false);
      const errMsg = err?.response?.data?.message || err?.message || 'Error al cancelar';
      message.error(errMsg);
    }
  };

  const [stampingPayment, setStampingPayment] = useState({});

  const handleEditPayment = (payment) => {
    // Si la factura tiene un método para abrir el modal, lo usamos
    if (invoice.openPaymentModal) {
        invoice.openPaymentModal(payment);
    }
  };

  const handleStampPayment = async (paymentId) => {
    Modal.confirm({
      title: 'Timbrar Pago',
      content: '¿Estás seguro de que deseas timbrar este pago? Una vez timbrado no se podrá editar ni eliminar.',
      okText: 'Timbrar',
      cancelText: 'Cancelar',
      onOk: async () => {
        const normalizeApiError = (err) => {
          let errMsg =
            err?.response?.data?.error ||
            err?.response?.data?.message ||
            err?.message ||
            'Error al timbrar el pago';
          
          if (typeof errMsg === 'string' && errMsg.startsWith('Error HTTP:')) {
            try {
              const jsonPart = errMsg.substring('Error HTTP:'.length, errMsg.indexOf(' - {')).trim();
              const errorObj = JSON.parse(jsonPart);
              if (errorObj && errorObj.message) {
                errMsg = errorObj.message;
              }
            } catch (e) {
              // noop
            }
          }
          
          return errMsg;
        };

        try {
          const payment = payments.find((p) => p?.id === paymentId);
          if (payment?.fecha_pago) {
            const fechaPago = moment(payment.fecha_pago);
            const now = moment();
            if (fechaPago.isAfter(now, 'day')) {
              message.error('La fecha de pago no puede ser futura. Edita el pago y corrige la fecha antes de timbrar.');
              return;
            }
          }

          setStampingPayment(prev => ({ ...prev, [paymentId]: true }));
          await userService.stampPayment(paymentId);
          message.success('Pago timbrado exitosamente');
          // Recargar datos
          const resp = await userService.getInvoice(id);
          setInvoice(resp.data || resp);
          
          setPaymentsLoading(true);
          const paymentsResp = await userService.getPayments(100, 0);
          const allPayments = paymentsResp?.data?.payments ?? [];
          const related = allPayments.filter(
            (p) => Array.isArray(p.documents) && p.documents.some((d) => d?.invoice?.id == id)
          );
          setPayments(related);
          setPaymentsLoading(false);
        } catch (error) {
          console.error('Error stamping payment:', error);
          message.error(normalizeApiError(error));
        } finally {
          setStampingPayment(prev => ({ ...prev, [paymentId]: false }));
        }
      }
    });
  };

  const handleDeletePayment = async (paymentId) => {
    try {
      await userService.deletePayment(paymentId);
      message.success('Pago eliminado');
      loadPayments(invoice.id);
    } catch (err) {
      console.error(err);
      message.error('Error al eliminar el pago');
    }
  };

  const handleCancelPayment = (paymentId) => {
    // Reutilizamos el modal de cancelación existente, pero adaptado para pagos si fuera necesario
    // O mostramos confirmación simple si la cancelación de pagos es directa
    // Por ahora, asumimos que se cancela igual que una factura normal
    setCancelModalVisible(true);
    // TODO: Adaptar handleConfirmCancel para manejar pagos o facturas según contexto
  };

  const [emailModalVisible, setEmailModalVisible] = useState(false);
  const [emailSubject, setEmailSubject] = useState('');
  const [emailBody, setEmailBody] = useState('');
  const [sendingEmail, setSendingEmail] = useState(false);
  const [emailForm] = Form.useForm();

  const handleEmailClick = () => {
    const clientEmail = invoice?.receiver?.email || '';
    emailForm.setFieldsValue({ recipient_email: clientEmail });
    
    // Default subject and body
    const serieFolio = (invoice?.serie || '') + (invoice?.folio || '');
    const companyName = invoice?.company?.business_name || 'Mi Factura';
    
    const defaultSubject = `Factura ${serieFolio} de ${companyName}`;
    const defaultBody = `Adjunto encontrará los archivos XML y PDF de su factura.\n\nTotal: $${Number(invoice?.total || 0).toFixed(2)} ${invoice?.moneda || 'MXN'}\n\nSaludos cordiales.`;
    
    setEmailSubject(defaultSubject);
    setEmailBody(defaultBody);
    
    emailForm.setFieldsValue({
      subject: defaultSubject,
      body: defaultBody
    });
    
    setEmailModalVisible(true);
  };

  const handleSendEmail = async (values) => {
    setSendingEmail(true);
    try {
      await userService.sendInvoiceEmail(id, {
        recipients: [values.recipient_email],
        subject: values.subject,
        body: values.body
      });
      message.success('Correo enviado exitosamente');
      setEmailModalVisible(false);
    } catch (err) {
      console.error(err);
      message.error(err.response?.data?.message || 'Error al enviar el correo');
    } finally {
      setSendingEmail(false);
    }
  };

  const downloadResponseFile = (response, defaultFilename) => {
    const contentType = response.headers['content-type'] || 'application/octet-stream';
    const disposition = response.headers['content-disposition'] || '';
    const match = /filename="?([^";]+)"?/i.exec(disposition);
    const filename = match?.[1] || defaultFilename;

    let blob;
    const data = response.data;
    if (data instanceof Blob) {
      blob = data;
    } else if (data instanceof ArrayBuffer) {
      blob = new Blob([data], { type: contentType });
    } else {
      blob = new Blob([data], { type: contentType });
    }

    const url = window.URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = filename;
    a.click();
    window.URL.revokeObjectURL(url);
  };

  const [downloadingXml, setDownloadingXml] = useState(false);
  const [downloadingPdf, setDownloadingPdf] = useState(false);

  const handleDownloadXml = async () => {
    try {
      setDownloadingXml(true);
      const resp = await userService.downloadInvoiceXml(id);
      downloadResponseFile(resp, `CFDI_${invoice?.serie || ''}_${invoice?.folio || ''}_${invoice?.uuid || ''}.xml`);
    } catch (err) {
      message.error('No se pudo descargar el XML.');
    } finally {
      setDownloadingXml(false);
    }
  };

  const downloadBase64Pdf = (base64Content, filename) => {
    if (!base64Content) {
      message.error('El PDF no se recibió correctamente.');
      return;
    }
    const cleaned = base64Content.includes(',')
      ? base64Content.split(',').pop()
      : base64Content;
    const byteChars = atob(cleaned);
    const byteNums = new Array(byteChars.length);
    for (let i = 0; i < byteChars.length; i++) {
      byteNums[i] = byteChars.charCodeAt(i);
    }
    const byteArray = new Uint8Array(byteNums);
    const blob = new Blob([byteArray], { type: 'application/pdf' });
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = filename;
    a.click();
    window.URL.revokeObjectURL(url);
  };

  const handleDownloadPdf = async () => {
    try {
      setDownloadingPdf(true);
      const result = await userService.generateInvoicePdf(id, { templateId: 'complitcfdiv40' });
      const base64 = result?.data?.pdf_content;
      const docPrefix = invoice?.tipo_comprobante === 'N' ? 'Nomina_' : 'CFDI_';
      const filename = `${docPrefix}${invoice?.serie || ''}_${invoice?.folio || ''}_${invoice?.uuid || ''}.pdf`;
      downloadBase64Pdf(base64, filename);
    } catch (err) {
      message.error('No se pudo generar/descargar el PDF.');
    } finally {
      setDownloadingPdf(false);
    }
  };
  const handleDownloadPaymentsPdf = async ({id}) => {
    try {
      const result = await userService.generatePaymentsPdf(id, { templateId: 'payment20' });
      const base64 = result?.data?.pdf_content;
      const docPrefix = invoice?.tipo_comprobante === 'N' ? 'Nomina_' : 'Pago_';
      const filename = `${docPrefix}_${invoice?.serie || ''}_${invoice?.folio || ''}_${invoice?.uuid || ''}.pdf`;
      downloadBase64Pdf(base64, filename);
    } catch (err) {
      message.error('No se pudo generar/descargar el PDF.');
    }
  };
  const handleDownloadPaymentsXml = async ({id}) => {
    try {
      const resp = await userService.downloadPaymentsXml(id);
      downloadResponseFile(resp, `Pagos_${invoice?.serie || ''}_${invoice?.folio || ''}_${invoice?.uuid || ''}.xml`);
    } catch (err) {
      message.error('No se pudo descargar el XML.');
    }
  };
  

  const handleDownloadAcuse = async () => {
    try {
      const resp = await userService.getCancellationStatus(id);
      const payload = resp?.data ?? resp;
      const info = payload?.data ?? payload;

      const acuse =
        info?.acuse_cancelacion ??
        info?.acuse_sat ??
        info?.acuse_completo;

      if (!acuse) {
        message.warning('No hay acuse de cancelación disponible.');
        return;
      }

      let content = acuse;
      let mime = 'text/plain';
      let filename = `Acuse_${invoice?.serie || ''}_${invoice?.folio || ''}_${invoice?.uuid || ''}.txt`;

      if (typeof acuse === 'object') {
        content = JSON.stringify(acuse, null, 2);
        mime = 'application/json';
        filename = filename.replace('.txt', '.json');
      } else if (typeof acuse === 'string') {
        const trimmed = acuse.trim();
        if (trimmed.startsWith('<')) {
          mime = 'application/xml';
          filename = filename.replace('.txt', '.xml');
          content = trimmed;
        } else if (trimmed.startsWith('data:')) {
          const [meta, base] = trimmed.split(',');
          const typeMatch = meta.match(/data:(.*?);base64/);
          const type = typeMatch?.[1] || 'application/octet-stream';
          const bytes = atob(base);
          const buf = new Uint8Array(bytes.length);
          for (let i = 0; i < bytes.length; i++) buf[i] = bytes.charCodeAt(i);
          const blob = new Blob([buf], { type });
          const url = window.URL.createObjectURL(blob);
          const a = document.createElement('a');
          a.href = url;
          a.download = filename;
          a.click();
          window.URL.revokeObjectURL(url);
          return;
        }
      }

      const blob = new Blob([content], { type: mime });
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = filename;
      a.click();
      window.URL.revokeObjectURL(url);
    } catch (err) {
      message.error('No se pudo descargar el acuse de cancelación.');
    }
  };

  const handleDelete = () => {
    Modal.confirm({
      title: 'Eliminar borrador',
      content: '¿Estás seguro de que deseas eliminar este borrador de factura? Esta acción no se puede deshacer.',
      okText: 'Eliminar',
      okType: 'danger',
      cancelText: 'Cancelar',
      onOk: async () => {
        try {
          await userService.deleteInvoice(id);
          message.success('Borrador eliminado correctamente');
          navigate(backPath);
        } catch (error) {
          console.error('Error deleting invoice:', error);
          message.error(error.response?.data?.message || 'Error al eliminar el borrador');
        }
      },
    });
  };

  const copyUuid = async () => {
    if (!invoice?.uuid) return;
    try {
      await navigator.clipboard.writeText(invoice.uuid);
      message.success('UUID copiado');
    } catch (e) {
      message.error('No se pudo copiar el UUID');
    }
  };

  if (loading) {
    return (
      <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '40vh' }}>
        <Spin size="large" tip="Cargando factura..." />
      </div>
    );
  }

  if (!invoice) {
    return (
      <Card style={{ maxWidth: 480, margin: '48px auto', textAlign: 'center' }}>
        <Title level={4} style={{ marginTop: 0 }}>Factura no encontrada</Title>
        <Text type="secondary">La factura que buscas no existe o fue eliminada.</Text>
        <div style={{ marginTop: 24 }}>
          <Button type="primary" icon={<ArrowLeftOutlined />} onClick={() => navigate(backPath)}>Regresar</Button>
        </div>
      </Card>
    );
  }

  const meta = statusMeta(invoice?.status);
  const serieFolio = `${invoice.serie || ''}${invoice.folio || ''}` || '—';
  const isPPD = invoice?.metodo_pago === 'PPD';
  const paidPct = invoiceTotal > 0 ? Math.min(Math.round((totalPaid / invoiceTotal) * 100), 100) : 0;

  const conceptos = Array.isArray(invoice?.conceptos) ? invoice.conceptos : [];
  const conceptoColumns = [
    {
      title: 'Descripción',
      dataIndex: 'descripcion',
      key: 'descripcion',
      render: (v, r) => (
        <div>
          <div>{v || '—'}</div>
          {(r?.clave_prod_serv || r?.no_identificacion) && (
            <Text type="secondary" style={{ fontSize: 12 }}>
              {r?.clave_prod_serv || ''}{r?.no_identificacion ? ` · ${r.no_identificacion}` : ''}
            </Text>
          )}
        </div>
      ),
    },
    {
      title: 'Cantidad',
      dataIndex: 'cantidad',
      key: 'cantidad',
      align: 'right',
      width: 90,
      render: (v) => <span style={{ fontVariantNumeric: 'tabular-nums' }}>{Number(v || 0).toLocaleString('es-MX')}</span>,
    },
    { title: 'Unidad', dataIndex: 'unidad', key: 'unidad', width: 110, render: (v) => v || '—' },
    {
      title: 'Valor unitario',
      dataIndex: 'valor_unitario',
      key: 'valor_unitario',
      align: 'right',
      width: 130,
      render: (v) => <span style={{ fontVariantNumeric: 'tabular-nums' }}>{formatMoney(v, invoice?.moneda || 'MXN')}</span>,
    },
    {
      title: 'Descuento',
      dataIndex: 'descuento',
      key: 'descuento',
      align: 'right',
      width: 120,
      render: (v) => <span style={{ fontVariantNumeric: 'tabular-nums' }}>{formatMoney(v, invoice?.moneda || 'MXN')}</span>,
    },
    {
      title: 'Importe',
      dataIndex: 'importe',
      key: 'importe',
      align: 'right',
      width: 130,
      render: (v) => <span style={{ fontVariantNumeric: 'tabular-nums' }}>{formatMoney(v, invoice?.moneda || 'MXN')}</span>,
    },
  ];

  const paymentColumns = [
    {
      title: 'Fecha de pago',
      dataIndex: 'fecha_pago',
      key: 'fecha_pago',
      render: (val) => (val ? new Date(val).toLocaleDateString('es-MX') : '—'),
    },
    {
      title: 'Monto',
      dataIndex: 'monto',
      key: 'monto',
      align: 'right',
      render: (val, record) => (
        <span style={{ fontVariantNumeric: 'tabular-nums' }}>{formatMoney(val, record?.moneda || 'MXN')}</span>
      ),
    },
    { title: 'Forma de pago', dataIndex: 'forma_pago', key: 'forma_pago' },
    {
      title: 'Estado',
      dataIndex: 'status',
      key: 'status',
      render: (s) => {
        const m = statusMeta(s);
        return <Tag color={m.color}>{m.label}</Tag>;
      },
    },
    { title: 'Serie', dataIndex: 'serie', key: 'serie', render: (v) => v || '—' },
    { title: 'Folio', dataIndex: 'folio', key: 'folio', render: (v) => (v == null || v === '' ? '—' : String(v)) },
    { title: 'UUID', dataIndex: 'uuid', key: 'uuid', render: (v) => v || '—' },
    {
      title: 'Documentos (DR)',
      key: 'doc',
      render: (_, record) => {
        const items = (record.documents || []).filter((d) => d?.invoice?.id === invoice?.id);
        if (!items.length) return '—';
        return items.map((d, i) => (
          <div key={i}>
            {d.invoice?.serie || ''}-{d.invoice?.folio || ''} · Parcialidad {d.num_parcialidad} · Pagado{' '}
            {formatMoney(d.importe_pagado)}
          </div>
        ));
      },
    },
    {
      title: 'Acciones',
      key: 'actions',
      width: 140,
      render: (_, record) => (
        <Space size="small">
          {record.status === 'timbrada' && (
            <>
              <Tooltip title="Descargar XML">
                <Button shape="circle" size="small" icon={<FileTextOutlined />} onClick={() => handleDownloadPaymentsXml({ id: record.id })} />
              </Tooltip>
              <Tooltip title="Descargar PDF">
                <Button shape="circle" size="small" type="primary" icon={<FilePdfOutlined />} onClick={() => handleDownloadPaymentsPdf({ id: record.id })} />
              </Tooltip>
              {!isReadOnly && (
                <Tooltip title="Cancelar">
                  <Button shape="circle" size="small" danger icon={<DeleteOutlined />} onClick={() => handleCancelPayment(record.id)} disabled={record.status === 'cancelada'} />
                </Tooltip>
              )}
            </>
          )}
          {!isReadOnly && record.status !== 'timbrada' && (
            <>
              <Tooltip title="Editar">
                <Button shape="circle" size="small" icon={<EditOutlined />} onClick={() => handleEditPayment(record)} />
              </Tooltip>
              <Tooltip title="Timbrar">
                <Button shape="circle" size="small" type="primary" icon={<CloudUploadOutlined />} loading={stampingPayment[record.id]} onClick={() => handleStampPayment(record.id)} />
              </Tooltip>
              <Popconfirm title="¿Eliminar pago?" description="Esta acción no se puede deshacer." onConfirm={() => handleDeletePayment(record.id)} okText="Sí" cancelText="No">
                <Tooltip title="Eliminar">
                  <Button shape="circle" size="small" danger icon={<DeleteOutlined />} />
                </Tooltip>
              </Popconfirm>
            </>
          )}
        </Space>
      ),
    },
  ];

  return (
    <>
    <div style={{ maxWidth: 1100, margin: '0 auto' }}>
      <Button type="text" icon={<ArrowLeftOutlined />} onClick={() => navigate(backPath)} style={{ marginBottom: 12, paddingLeft: 0 }}>
        Regresar
      </Button>

      {/* HERO: identidad + total + metadatos + acciones */}
      <Card style={{ marginBottom: 16 }} styles={{ body: { padding: screens.xs ? 16 : 24 } }}>
        <Row gutter={[16, 16]} justify="space-between" align="top" wrap>
          <Col xs={24} md={16}>
            <Space size={8} wrap style={{ marginBottom: 4 }}>
              <Tag color={meta.color} icon={meta.icon} style={{ margin: 0 }}>{meta.label}</Tag>
              {invoice?.metodo_pago && <Tag color={isPPD ? 'gold' : 'default'}>{invoice.metodo_pago}</Tag>}
            </Space>
            <Title level={2} style={{ margin: '4px 0 2px' }}>Factura {serieFolio}</Title>
            {invoice?.uuid ? (
              <Space size={6} align="center" style={{ flexWrap: 'wrap' }}>
                <Text type="secondary" style={{ fontVariantNumeric: 'tabular-nums', wordBreak: 'break-all' }}>{invoice.uuid}</Text>
                <Tooltip title="Copiar UUID">
                  <Button type="text" size="small" icon={<CopyOutlined />} onClick={copyUuid} />
                </Tooltip>
              </Space>
            ) : (
              <Text type="secondary">Sin folio fiscal (borrador)</Text>
            )}
          </Col>
          <Col xs={24} md={8} style={{ textAlign: screens.md ? 'right' : 'left' }}>
            <Statistic
              title="Total"
              value={Number(invoice?.total || 0)}
              precision={2}
              prefix="$"
              suffix={invoice?.moneda || 'MXN'}
              valueStyle={{ fontWeight: 700 }}
            />
          </Col>
        </Row>

        <Divider style={{ margin: '16px 0' }} />

        <Row gutter={[24, 12]}>
          <Col xs={12} md={6}>
            <Text type="secondary" style={{ fontSize: 12, display: 'block' }}>Emisor</Text>
            <Text strong>{invoice.company?.tax_name || '—'}</Text>
          </Col>
          <Col xs={12} md={6}>
            <Text type="secondary" style={{ fontSize: 12, display: 'block' }}>Receptor</Text>
            <Text strong>{invoice.receiver?.tax_name || '—'}</Text>
          </Col>
          <Col xs={12} md={6}>
            <Text type="secondary" style={{ fontSize: 12, display: 'block' }}>Método de pago</Text>
            <Text strong>{invoice.metodo_pago || '—'}</Text>
          </Col>
          <Col xs={12} md={6}>
            <Text type="secondary" style={{ fontSize: 12, display: 'block' }}>Fecha de creación</Text>
            <Text strong>{invoice.created_at ? new Date(invoice.created_at).toLocaleDateString('es-MX') : '—'}</Text>
          </Col>
        </Row>

        <Divider style={{ margin: '16px 0' }} />

        <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8, alignItems: 'center' }}>
          <Button type="primary" icon={<FilePdfOutlined />} onClick={handleDownloadPdf} disabled={!invoice || downloadingPdf} loading={downloadingPdf}>
            Descargar PDF
          </Button>
          <Button icon={<FileTextOutlined />} onClick={handleDownloadXml} disabled={!invoice || invoice.status === 'borrador' || downloadingXml} loading={downloadingXml}>
            Descargar XML
          </Button>
          {invoice?.status === 'timbrada' && (
            <Button icon={<MailOutlined />} onClick={handleEmailClick}>Enviar por Correo</Button>
          )}
          {!isReadOnly && !isInvoiceFullyPaid && <AddPaymentModal invoice={invoice} onCreated={refreshInvoice} />}

          <div style={{ flex: 1 }} />

          {!isReadOnly && invoice?.status === 'borrador' && (
            <Button danger icon={<DeleteOutlined />} onClick={handleDelete}>Eliminar Borrador</Button>
          )}
          {invoice?.status === 'cancelada' ? (
            <Button icon={<FileTextOutlined />} onClick={handleDownloadAcuse} disabled={!invoice}>Descargar acuse</Button>
          ) : (
            !isReadOnly && invoice?.status === 'timbrada' && (
              <Button danger icon={<CloseCircleOutlined />} onClick={openCancelModal}>Cancelar</Button>
            )
          )}
        </div>
      </Card>

      {/* Progreso de cobro para PPD */}
      {isPPD && (
        <Card size="small" style={{ marginBottom: 16 }} title={<Space><CreditCardOutlined />Progreso de cobro</Space>}>
          <Row gutter={[16, 16]} align="middle">
            <Col xs={8}>
              <Statistic title="Total" value={invoiceTotal} precision={2} prefix="$" valueStyle={{ fontSize: 18 }} />
            </Col>
            <Col xs={8}>
              <Statistic title="Pagado" value={totalPaid} precision={2} prefix="$" valueStyle={{ fontSize: 18, color: token.colorSuccess }} />
            </Col>
            <Col xs={8}>
              <Statistic title="Pendiente" value={Math.max(invoiceTotal - totalPaid, 0)} precision={2} prefix="$" valueStyle={{ fontSize: 18, color: isInvoiceFullyPaid ? token.colorSuccess : token.colorError }} />
            </Col>
          </Row>
          <Progress percent={paidPct} status={isInvoiceFullyPaid ? 'success' : 'active'} style={{ marginTop: 8 }} />
        </Card>
      )}

      {/* Emisor / Receptor */}
      <Row gutter={[16, 16]} style={{ marginBottom: 16 }}>
        <Col xs={24} md={12}>
          <Card size="small" title={<Space><BankOutlined />Emisor</Space>} style={{ height: '100%' }}>
            <Descriptions column={1} size="small">
              <Descriptions.Item label="Empresa">{invoice.company?.tax_name || '—'}</Descriptions.Item>
              <Descriptions.Item label="RFC">{invoice.company?.tax_id || '—'}</Descriptions.Item>
            </Descriptions>
          </Card>
        </Col>
        <Col xs={24} md={12}>
          <Card size="small" title={<Space><UserOutlined />Receptor</Space>} style={{ height: '100%' }}>
            <Descriptions column={1} size="small">
              <Descriptions.Item label="Cliente">{invoice.receiver?.tax_name || '—'}</Descriptions.Item>
              <Descriptions.Item label="RFC">{invoice.receiver?.tax_id || '—'}</Descriptions.Item>
              <Descriptions.Item label="Correo">{invoice.receiver?.email || '—'}</Descriptions.Item>
            </Descriptions>
          </Card>
        </Col>
      </Row>

      <Card
        size="small"
        title={<Space><FileTextOutlined />Conceptos</Space>}
        extra={conceptos.length ? <Text type="secondary">{conceptos.length} {conceptos.length === 1 ? 'partida' : 'partidas'}</Text> : null}
        style={{ marginBottom: 16 }}
      >
        <ResponsiveTable
          columns={conceptoColumns}
          dataSource={conceptos}
          rowKey={(r, i) => r?.id ?? i}
          pagination={false}
          size="small"
          scroll={{ y: 320, x: 'max-content' }}
          locale={{ emptyText: 'Esta factura no tiene conceptos' }}
        />
      </Card>

      {/* Pagos relacionados: ahora en su propia tarjeta, no incrustados */}
      {payments.length > 0 && (
        <Card size="small" title={<Space><CreditCardOutlined />Pagos relacionados</Space>} style={{ marginBottom: 16 }}>
          <ResponsiveTable
            columns={paymentColumns}
            dataSource={payments}
            rowKey="id"
            loading={paymentsLoading}
            pagination={false}
            size="small"
            scroll={{ x: 'max-content' }}
          />
        </Card>
      )}
    </div>

    <Modal
      title="Cancelar CFDI"
      open={cancelModalVisible}
      onCancel={() => setCancelModalVisible(false)}
      footer={null}
      destroyOnClose
    >
      <Form form={cancelForm} layout="vertical" onFinish={handleConfirmCancel}>
        <Form.Item
          label="Motivo de cancelación"
          name="motivo"
          rules={[{ required: true, message: 'Selecciona un motivo' }]}
        >
          <Select
            placeholder="Selecciona motivo (01–04)"
            onChange={setSelectedMotivo}
            loading={!motives.length}
          >
            {(motives.length ? motives : allowedCodes.map((code) => ({ code, description: code }))).map(
              (m) => (
                <Select.Option key={m.code} value={m.code}>
                  {m.code} - {m.description || m.code}
                </Select.Option>
              )
            )}
          </Select>
        </Form.Item>

        {(selectedMotivo === '01' || selectedMotivo === '04') && (
          <Form.Item
            label="UUID de sustitución"
            name="uuid_sustitucion"
            rules={[{ required: true, message: 'Requiere UUID de sustitución para motivo 01 o 04' }]}
          >
            <Input placeholder="xxxxxxxx-xxxx-xxxx-xxxx-xxxxxxxxxxxx" />
          </Form.Item>
        )}

        <Form.Item>
          <Button type="primary" htmlType="submit" loading={cancelling}>
            Confirmar cancelación
          </Button>
        </Form.Item>
      </Form>
    </Modal>

    <Modal
      title="Enviar Factura por Correo"
      open={emailModalVisible}
      onCancel={() => setEmailModalVisible(false)}
      footer={null}
      destroyOnClose
    >
      <Form form={emailForm} layout="vertical" onFinish={handleSendEmail}>
        <Form.Item
          label="Correo destinatario"
          name="recipient_email"
          rules={[
            { required: true, message: 'Ingresa el correo' },
            { type: 'email', message: 'Correo inválido' },
          ]}
        >
          <Input placeholder="correo@dominio.com" />
        </Form.Item>

        <Form.Item
          label="Asunto"
          name="subject"
          rules={[{ required: true, message: 'El asunto es requerido' }]}
        >
          <Input />
        </Form.Item>

        <Form.Item
          label="Mensaje"
          name="body"
        >
          <Input.TextArea rows={4} />
        </Form.Item>

        <Form.Item>
          <Button type="primary" htmlType="submit" loading={sendingEmail} block>
            Enviar Correo
          </Button>
        </Form.Item>
      </Form>
    </Modal>
    </>
  );
};

export default InvoiceDetail;
